/**
 * RelationshipsPanel
 * Shows parents, partners, children, and siblings of a person.
 * Allows navigating to related persons and removing relationships.
 */
import { useState } from 'react'
import { api } from '../../services/api.js'
import PlaceInput from '../shared/PlaceInput.jsx'
import PartialDateInput from '../shared/PartialDateInput.jsx'
import './RelationshipsPanel.css'

const LABELS = {
  parent:  'Parents',
  partner: 'Partners',
  child:   'Children',
  sibling: 'Siblings',
}

export default function RelationshipsPanel({ person, treePersons, relationships, treeId, onNavigate, onRelationshipRemoved, onRelationshipUpdated }) {
  const [removeError, setRemoveError] = useState(null)
  const [editingMarriage, setEditingMarriage] = useState(null) // rel.id being edited

  if (!relationships?.length || !treePersons?.length) return null

  const personMap = new Map(treePersons.map(p => [p.id, p]))

  // Bucket relationships by type relative to this person
  const buckets = { parent: [], partner: [], child: [], sibling: [] }

  for (const rel of relationships) {
    const isA = rel.person_a_id === person.id
    const isB = rel.person_b_id === person.id
    if (!isA && !isB) continue

    if (rel.type === 'parent_child') {
      if (isA) buckets.child.push({ rel, otherId: rel.person_b_id })
      else     buckets.parent.push({ rel, otherId: rel.person_a_id })
    } else if (rel.type === 'partner') {
      const otherId = isA ? rel.person_b_id : rel.person_a_id
      buckets.partner.push({ rel, otherId })
    } else if (rel.type === 'sibling') {
      const otherId = isA ? rel.person_b_id : rel.person_a_id
      buckets.sibling.push({ rel, otherId })
    }
  }

  const hasAny = Object.values(buckets).some(b => b.length > 0)
  if (!hasAny) return null

  function personLabel(p) {
    if (!p) return 'Unknown'
    const name = [p.preferred_name || p.first_name, p.last_name].filter(Boolean).join(' ') || 'Unknown'
    const year = p.birth_date?.match(/\d{4}/)?.[0]
    return year ? `${name} (${year})` : name
  }

  async function remove(rel) {
    if (!confirm('Remove this relationship?')) return
    setRemoveError(null)
    try {
      await api.delete(`/relationships/${rel.id}`)
      onRelationshipRemoved(rel.id)
    } catch (err) {
      setRemoveError(err.message || 'Failed to remove relationship')
    }
  }

  return (
    <div className="relationships-panel">
      <div className="relationships-panel__title">Family</div>
      {removeError && (
        <div className="form-error" style={{ marginBottom: '0.5rem' }}>
          {removeError}
          <button
            className="btn btn-ghost btn-sm"
            style={{ marginLeft: '0.75rem', padding: '0.15rem 0.5rem' }}
            onClick={() => setRemoveError(null)}
          >Dismiss</button>
        </div>
      )}
      {Object.entries(buckets).map(([type, items]) => {
        if (!items.length) return null
        // Sort partners chronologically by marriage_date (then by
        // rel.id for determinism). Current / never-ended partners keep
        // their natural order; ended ones fall after by end_date.
        const sorted = type !== 'partner' ? items : [...items].sort((a, b) => {
          const ay = a.rel.marriage_date?.match(/\d{4}/)?.[0] || '9999'
          const by = b.rel.marriage_date?.match(/\d{4}/)?.[0] || '9999'
          if (ay !== by) return ay.localeCompare(by)
          return (a.rel.id || '').localeCompare(b.rel.id || '')
        })
        return (
          <div key={type} className="rel-group">
            <div className="rel-group__label">{LABELS[type]}</div>
            {sorted.map(({ rel, otherId }) => {
              const other = personMap.get(otherId)
              const mYear = rel.marriage_date?.match(/\d{4}/)?.[0]
              const eYear = rel.end_date?.match(/\d{4}/)?.[0]
              const isPartner = rel.type === 'partner'
              const hasMarriage = mYear || rel.marriage_place
              const hasEnd = !!rel.end_reason || !!rel.end_date
              const isEditing = editingMarriage === rel.id
              const status = isPartner ? statusFor(rel) : null
              return (
                <div key={rel.id} className="rel-person">
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
                      <button
                        className="rel-person__name"
                        onClick={() => onNavigate(otherId)}
                      >
                        {personLabel(other)}
                      </button>
                      {status && <StatusChip status={status} />}
                    </div>
                    {isPartner && !isEditing && (
                      <button
                        onClick={() => setEditingMarriage(rel.id)}
                        style={{
                          display: 'block', background: 'none', border: 'none',
                          padding: '0.15rem 0', fontSize: '0.75rem',
                          color: 'var(--text-muted)', cursor: 'pointer',
                          textAlign: 'left',
                        }}
                        title="Edit marriage details"
                      >
                        {hasMarriage
                          ? `Married${mYear ? ` ${mYear}` : ''}${rel.marriage_place ? ` in ${rel.marriage_place}` : ''}${hasEnd ? ` · ended${eYear ? ` ${eYear}` : ''}` : ''} · edit`
                          : <span style={{ color: 'var(--green)' }}>+ add marriage / end details</span>}
                      </button>
                    )}
                    {isPartner && isEditing && (
                      <MarriageEditor
                        rel={rel}
                        onCancel={() => setEditingMarriage(null)}
                        onSaved={updated => {
                          setEditingMarriage(null)
                          onRelationshipUpdated?.(updated)
                        }}
                      />
                    )}
                  </div>
                  <button
                    className="rel-person__remove"
                    onClick={() => remove(rel)}
                    title="Remove relationship"
                  >
                    ✕
                  </button>
                </div>
              )
            })}
          </div>
        )
      })}
    </div>
  )
}

// Status derived from end_reason / end_date. 'current' is the default
// when neither is set — historically almost every partnership is
// assumed to be a marriage still in force unless stated otherwise.
function statusFor(rel) {
  if (rel.end_reason === 'divorce')   return 'divorced'
  if (rel.end_reason === 'widowed')   return 'widowed'
  if (rel.end_reason === 'separated') return 'separated'
  if (rel.end_reason === 'annulled')  return 'annulled'
  if (rel.end_date)                   return 'ended'
  return 'current'
}

const STATUS_STYLE = {
  current:   { bg: '#e6f5ec', fg: '#2d5a3e', label: 'Current' },
  divorced:  { bg: '#f2f2f2', fg: '#5a5a5a', label: 'Divorced' },
  widowed:   { bg: '#eee8dc', fg: '#7a6a40', label: 'Widowed' },
  separated: { bg: '#fff7e0', fg: '#8a6400', label: 'Separated' },
  annulled:  { bg: '#f2f2f2', fg: '#5a5a5a', label: 'Annulled' },
  ended:     { bg: '#f2f2f2', fg: '#5a5a5a', label: 'Ended' },
}

function StatusChip({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.ended
  return (
    <span style={{
      display: 'inline-block',
      padding: '0.1rem 0.5rem',
      borderRadius: 999,
      background: s.bg, color: s.fg,
      fontSize: '0.68rem', fontWeight: 600,
      letterSpacing: '0.04em',
    }}>{s.label}</span>
  )
}

// Inline editor for marriage + end metadata on a partner relationship.
// PATCHes /relationships/:id and hands the updated row back via onSaved
// so the parent can refresh relationship state without a full reload.
function MarriageEditor({ rel, onCancel, onSaved }) {
  const [date, setDate] = useState(rel.marriage_date || '')
  const [place, setPlace] = useState(rel.marriage_place || '')
  const [endDate, setEndDate] = useState(rel.end_date || '')
  const [endReason, setEndReason] = useState(rel.end_reason || '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  async function save() {
    setSaving(true); setError(null)
    try {
      const updated = await api.patch(`/relationships/${rel.id}`, {
        marriage_date:  date.trim() || null,
        marriage_place: place.trim() || null,
        end_date:       endDate.trim() || null,
        end_reason:     endReason || null,
      })
      onSaved(updated)
    } catch (err) {
      setError(err.message || 'Save failed')
      setSaving(false)
    }
  }

  return (
    <div style={{ marginTop: '0.35rem', padding: '0.5rem 0.6rem', background: 'var(--paper)', border: '1px solid var(--border)', borderRadius: 6 }}>
      <label style={{ display: 'block', fontSize: '0.72rem', color: 'var(--text-muted)', margin: '0 0 0.25rem' }}>Marriage date</label>
      <PartialDateInput value={date} onChange={setDate} />
      <div style={{ marginTop: '0.4rem' }}>
        <PlaceInput
          placeholder="Marriage place — e.g. Amsterdam"
          value={place}
          onChange={setPlace}
          style={{ fontSize: '0.8rem', padding: '0.35rem 0.5rem' }}
        />
      </div>
      <div style={{ marginTop: '0.4rem' }}>
        <select
          className="form-input"
          value={endReason}
          onChange={e => setEndReason(e.target.value)}
          style={{ fontSize: '0.8rem', padding: '0.35rem 0.5rem' }}
        >
          <option value="">Still together</option>
          <option value="divorce">Divorced</option>
          <option value="widowed">Widowed</option>
          <option value="separated">Separated</option>
          <option value="annulled">Annulled</option>
        </select>
      </div>
      {endReason && (
        <div style={{ marginTop: '0.4rem' }}>
          <label style={{ display: 'block', fontSize: '0.72rem', color: 'var(--text-muted)', margin: '0 0 0.25rem' }}>End date</label>
          <PartialDateInput value={endDate} onChange={setEndDate} />
        </div>
      )}
      {error && <div style={{ color: '#b54040', fontSize: '0.75rem', marginTop: '0.3rem' }}>{error}</div>}
      <div style={{ marginTop: '0.4rem', display: 'flex', gap: '0.35rem' }}>
        <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button className="btn btn-ghost btn-sm" onClick={onCancel} disabled={saving}>Cancel</button>
      </div>
    </div>
  )
}
